import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { ReactComponent as FrydaLogo } from "./../../assets/logo/Fryda_logo.svg";
import { useTranslation } from "react-i18next";
import { ToastVariant, useToastContext } from "../../context";
import API from "../../services";

type GlobalLoginProps = {
    partnerName: string;
};

type RequestPartnerProps = {
    companyName: string;
    contactName: string;
    email: string;
    phone?: string;
    website?: string;
};

const GlobalLogin = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { addToast } = useToastContext();
    const [showRequestForm, setShowRequestForm] = useState(false);

    // Form for Partner Lookup
    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
    } = useForm<GlobalLoginProps>();

    // Form for Requesting Partner Environment
    const {
        register: registerRequest,
        handleSubmit: handleSubmitRequest,
        formState: { errors: errorsRequest, isSubmitting: isSubmittingRequest },
        reset: resetRequestForm,
    } = useForm<RequestPartnerProps>();

    const onLookupSubmit: SubmitHandler<GlobalLoginProps> = async (data) => {
        API.post(
            `v3/partner/getPartnerIdbyName`,
            { name: data.partnerName },
            (response) => {
                const partnerId = response.data.id;
                if (partnerId) {
                    navigate(`/${partnerId}/login`);
                } else {
                    addToast(ToastVariant.ERROR, {
                        title: "Partner Not Found",
                        description: "Could not find a partner environment with that name.",
                    });
                }
            },
            (error) => {
                console.error(error);
                const errorMessage = error?.error?.message || "An error occurred while looking up the partner.";
                addToast(ToastVariant.ERROR, {
                    title: "Lookup Failed",
                    description: errorMessage,
                });
            }
        );
    };

    const onRequestSubmit: SubmitHandler<RequestPartnerProps> = async (data) => {
        API.post(
            `v3/partner/request`,
            data as any, // Cast to any because the generic type might not match perfectly if service expects specific object shape
            () => {
                addToast(ToastVariant.SUCCESS, {
                    title: "Request Sent",
                    description: "Your request for a partner environment has been sent successfully.",
                });
                setShowRequestForm(false);
                resetRequestForm();
            },
            (error) => {
                console.error(error);
                const errorMessage = error?.error?.message || "An error occurred while sending the request.";
                addToast(ToastVariant.ERROR, {
                    title: "Request Failed",
                    description: errorMessage,
                });
            }
        );
    };

    if (showRequestForm) {
        return (
            <div className="min-h-screen bg-gradient-to-r from-[#E1E3F9] from-10% via-[#CDD1FF] via-30% to-[#E6EFFD] to-90% flex items-center justify-center">
                <div className="flex items-center justify-center flex-col w-full max-w-md px-4">
                    <FrydaLogo />
                    <div className="bg-white p-8 mt-6 rounded-lg shadow-2xl border-2 border-gray-600 w-full">
                        <h2 className="text-xl font-bold mb-4 text-center">Request Partner Environment</h2>
                        <form onSubmit={handleSubmitRequest(onRequestSubmit)}>
                            {/* Company Name */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700">Company Name</label>
                                <input
                                    type="text"
                                    {...registerRequest("companyName", { required: "Company name is required" })}
                                    className="mt-1 p-2 w-full border border-gray-300 rounded-md focus:outline-none"
                                />
                                <span className="text-red-500 text-xs">{errorsRequest.companyName?.message}</span>
                            </div>

                            {/* Contact Name */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700">Contact Name</label>
                                <input
                                    type="text"
                                    {...registerRequest("contactName", { required: "Contact name is required" })}
                                    className="mt-1 p-2 w-full border border-gray-300 rounded-md focus:outline-none"
                                />
                                <span className="text-red-500 text-xs">{errorsRequest.contactName?.message}</span>
                            </div>

                            {/* Email */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700">Business Email</label>
                                <input
                                    type="email"
                                    {...registerRequest("email", {
                                        required: "Email is required",
                                        pattern: {
                                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                            message: "Invalid email address",
                                        },
                                    })}
                                    className="mt-1 p-2 w-full border border-gray-300 rounded-md focus:outline-none"
                                />
                                <span className="text-red-500 text-xs">{errorsRequest.email?.message}</span>
                            </div>

                            {/* Phone */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700">Phone (Optional)</label>
                                <input
                                    type="tel"
                                    {...registerRequest("phone")}
                                    className="mt-1 p-2 w-full border border-gray-300 rounded-md focus:outline-none"
                                />
                            </div>

                            {/* Website */}
                            <div className="mb-5">
                                <label className="block text-sm font-medium text-gray-700">Website (Optional)</label>
                                <input
                                    type="url"
                                    {...registerRequest("website")}
                                    className="mt-1 p-2 w-full border border-gray-300 rounded-md focus:outline-none"
                                />
                            </div>

                            <div className="flex gap-4">
                                <button
                                    type="button"
                                    onClick={() => setShowRequestForm(false)}
                                    className="w-1/2 bg-gray-200 text-gray-800 p-3 rounded-md hover:bg-gray-300 transition-colors"
                                >
                                    Back
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSubmittingRequest}
                                    className="w-1/2 bg-black text-white p-3 rounded-md disabled:bg-gray-400"
                                >
                                    {isSubmittingRequest ? "Sending..." : "Send Request"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-r from-[#E1E3F9] from-10% via-[#CDD1FF] via-30% to-[#E6EFFD] to-90% flex items-center justify-center">
            <div className="flex items-center justify-center flex-col">
                <FrydaLogo />
                <div className="bg-white p-8 mt-6 rounded-lg shadow-2xl border-2 border-gray-600 w-96">
                    <h2 className="text-xl font-bold mb-6 text-center">Partner Login</h2>
                    <p className="text-gray-600 text-sm mb-6 text-center">
                        Enter your partner environment name to continue.
                    </p>
                    <form onSubmit={handleSubmit(onLookupSubmit)}>
                        <div className="mb-5">
                            <label htmlFor="partnerName" className="block text-sm font-medium text-gray-700">
                                Partner Environment Name
                            </label>
                            <input
                                type="text"
                                {...register("partnerName", { required: "Partner Name is required" })}
                                id="partnerName"
                                className="mt-1 p-2 w-full border border-gray-300 rounded-md focus:outline-none"
                                placeholder="e.g. Acme Corp"
                            />
                            <span className="text-red-500 text-xs">{errors?.partnerName?.message}</span>
                        </div>

                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="w-full bg-black text-white p-3 rounded-md disabled:bg-gray-400"
                        >
                            {isSubmitting ? "Looking up..." : "Next"}
                        </button>
                    </form>

                    <div className="flex items-center mt-6">
                        <div className="w-1/2 border-b border-black-50"></div>
                        <div className="w-1/2 border-b border-black-50"></div>
                    </div>

                    <div className="mt-6 text-center">
                        <p className="text-sm text-gray-600 mb-2">Don't have an environment?</p>
                        <button
                            type="button"
                            className="text-black font-semibold hover:underline"
                            onClick={() => setShowRequestForm(true)}
                        >
                            Request Partner Environment
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GlobalLogin;
